import { ref, Ref, watch } from "vue";
import Konva from "konva";
import { Vector2d } from "konva/lib/types";
import { isMobile } from "@/modules/common";
import { showToast } from "vant";
import i18n from "@/locales";

function getDistance(p1: Vector2d, p2: Vector2d) {
  return Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2));
}

function getCenter(p1: Vector2d, p2: Vector2d) {
  return {
    x: (p1.x + p2.x) / 2,
    y: (p1.y + p2.y) / 2,
  };
}

const useZoom = (
  stage: Ref<Konva.Stage | null>,
  layer: Ref<Konva.Layer | null>,
  enable: Ref<boolean>,
  isToast?: boolean
) => {
  const scaleBy = 1.05;
  let lastCenter: { x: number; y: number } | null = null;
  let lastDist = 0;
  let dragStopped = false;
  const zoomRate = ref(1);
  const offset = ref({ x: 0, y: 0 });
  const inMobile = isMobile();

  watch(
    enable,
    (enable) => {
      if (!stage.value) return;
      stage.value?.draggable(enable);
      layer.value?.find("Shape").forEach((shape) => {
        if (enable) {
          shape.setDraggable(false);
        } else if (!shape.attrs.lock) {
          shape.setDraggable(true);
        }
      });
    },
    { immediate: true }
  );

  watch(
    stage,
    (stage) => {
      if (!stage) return;
      // 鼠标滚动缩放
      if (!stage.draggable()) stage.draggable(enable.value);
      stage.on("wheel", (e) => {
        // stop default scrolling
        e.evt.preventDefault();

        const oldScale = stage.scaleX();
        const pointer = stage.getPointerPosition();

        const mousePointTo = {
          x: (pointer!.x - stage.x()) / oldScale,
          y: (pointer!.y - stage.y()) / oldScale,
        };

        // how to scale? Zoom in? Or zoom out?
        let direction = e.evt.deltaY > 0 ? 1 : -1;

        // when we zoom on trackpad, e.evt.ctrlKey is true
        // in that case lets revert direction
        if (e.evt.ctrlKey) {
          direction = -direction;
        }

        const newScale =
          direction > 0 ? oldScale * scaleBy : oldScale / scaleBy;

        stage.scale({ x: newScale, y: newScale });
        zoomRate.value = newScale;

        const newPos = {
          x: pointer!.x - mousePointTo.x * newScale,
          y: pointer!.y - mousePointTo.y * newScale,
        };
        stage.position(newPos);
        offset.value = newPos;
      });

      stage.on("touchmove", function (e) {
        e.evt.preventDefault();
        const touch1 = e.evt.touches[0];
        const touch2 = e.evt.touches[1];

        if (
          !touch2 &&
          enable.value &&
          !stage.isDragging() &&
          isToast &&
          isMobile()
        ) {
          showToast({
            message: i18n.global.t(`Message.twoFinger`),
          });
        }

        // we need to restore dragging, if it was cancelled by multi-touch
        if (touch1 && !touch2 && !stage.isDragging() && dragStopped) {
          stage.startDrag();
          dragStopped = false;
        }

        if (touch1 && touch2) {
          // if the stage was under Konva's drag&drop
          // we need to stop it, and implement our own pan logic with two pointers
          if (stage.isDragging()) {
            dragStopped = true;
            stage.stopDrag();
          }

          const p1 = {
            x: touch1.clientX,
            y: touch1.clientY,
          };
          const p2 = {
            x: touch2.clientX,
            y: touch2.clientY,
          };

          if (!lastCenter) {
            lastCenter = getCenter(p1, p2);
            return;
          }
          const newCenter = getCenter(p1, p2);

          const dist = getDistance(p1, p2);

          if (!lastDist) {
            lastDist = dist;
          }

          // local coordinates of center point
          const pointTo = {
            x: (newCenter.x - stage.x()) / stage.scaleX(),
            y: (newCenter.y - stage.y()) / stage.scaleX(),
          };

          const scale = stage.scaleX() * (dist / lastDist);

          stage.scaleX(scale);
          stage.scaleY(scale);
          zoomRate.value = scale;

          // calculate new position of the stage
          const dx = newCenter.x - lastCenter.x;
          const dy = newCenter.y - lastCenter.y;

          const newPos = {
            x: newCenter.x - pointTo.x * scale + dx,
            y: newCenter.y - pointTo.y * scale + dy,
          };

          stage.position(newPos);
          offset.value = newPos;

          lastDist = dist;
          lastCenter = newCenter;
        }
      });

      stage.on("touchend", function (e) {
        lastDist = 0;
        lastCenter = null;
      });

      stage.on("dragmove", function () {
        if (!enable.value) return;
        offset.value = stage.position();
      });
    },
    { immediate: true }
  );

  const resetZoom = () => {
    stage.value?.scale({ x: 1, y: 1 });
    stage.value?.position({ x: 0, y: 0 });
    zoomRate.value = 1;
    offset.value = { x: 0, y: 0 };
    stage.value?.batchDraw();
  };

  return { resetZoom, zoomRate, offset };
};

export default useZoom;
